import { useEffect, useState, type RefObject } from 'react';
import { useBuilderStore } from '../state/builderStore';
import { CanvasFrame, blockLabel, fieldLabel } from './CanvasFrame';
import { sectionDisplayName } from '../shell/sectionVariantLabel';
import { isCurrentSelection, resolveCanvasTarget, type CanvasTarget } from './canvasTarget';

/**
 * The canvas HOVER FRAME — moving the pointer over the canvas outlines (dashed) whatever a click there
 * would select: a whole section, or, once we're drilled into that section, the block / field hotspot under
 * the pointer. It reads NOTHING and writes NOTHING to the selection: the left rail and the right inspector
 * stay exactly where they are until you actually click. Hover is a preview, not a selection.
 *
 * The listeners are NATIVE and attached to the `[data-aqx-page]` root, so a mouse move re-renders only this
 * overlay — never the sections. `mouseover` bubbles (unlike `mouseenter`), so one delegated listener covers
 * every descendant; `mouseleave` on the root clears the frame when the pointer exits the page.
 */
export function HoverFrame({ pageRef }: { pageRef: RefObject<HTMLDivElement | null> }) {
  const selectMode = useBuilderStore((s) => s.selectMode);
  const selectedSectionId = useBuilderStore((s) => s.selectedSectionId);
  const selectedBlockId = useBuilderStore((s) => s.selectedBlockId);
  const focusedFieldId = useBuilderStore((s) => s.focusedFieldId);
  const sectionsById = useBuilderStore((s) => s.sectionsById);

  const [hover, setHover] = useState<CanvasTarget | null>(null);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return;

    const onOver = (e: MouseEvent) => {
      const next = resolveCanvasTarget(e.target as Element | null, {
        selectMode,
        selectedSectionId,
      });
      // `mouseover` fires on every element boundary crossed; only re-render when the RESOLVED target
      // actually changes (moving inside one block is a no-op).
      setHover((prev) => (prev?.el === next?.el && prev?.kind === next?.kind ? prev : next));
    };
    const onLeave = () => setHover(null);

    page.addEventListener('mouseover', onOver);
    page.addEventListener('mouseleave', onLeave);
    return () => {
      page.removeEventListener('mouseover', onOver);
      page.removeEventListener('mouseleave', onLeave);
    };
  }, [pageRef, selectMode, selectedSectionId]);

  // The drill-in depth changes with the selection (selecting a section unlocks its parts), so a stale
  // hover computed under the old depth must be re-resolved rather than kept.
  useEffect(() => {
    setHover((prev) =>
      prev ? resolveCanvasTarget(prev.el, { selectMode, selectedSectionId }) : prev,
    );
  }, [selectMode, selectedSectionId]);

  if (!hover) return null;
  // Already selected → the solid <SelectionFrame> / EditableSection outline owns it; don't double-draw.
  if (isCurrentSelection(hover, { selectedSectionId, selectedBlockId, focusedFieldId }))
    return null;

  const section = sectionsById[hover.sectionId];
  const label =
    hover.kind === 'field'
      ? fieldLabel(section, hover.id)
      : hover.kind === 'block'
        ? blockLabel(section, hover.id)
        : sectionDisplayName(section);

  // A section is full-bleed: its frame must be drawn INSIDE the box or the canvas edge clips it, and its
  // label goes to the right so it doesn't sit on top of the drag grip that appears in the same corner.
  const isSection = hover.kind === 'section';

  return (
    <CanvasFrame
      pageRef={pageRef}
      target={hover.el}
      label={label}
      variant="hover"
      zIndex={202}
      inset={isSection}
      labelSide={isSection ? 'right' : 'left'}
    />
  );
}
